import type { Option } from "@tento-lona/sheets";
import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css, Style } from "@tento-ui/component-styles";
import { DomUtils } from "@tento-ui/dom";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { Hotkeys } from "@tento-ui/hotkeys";

/**
 * Generic, content-agnostic expand popup. Provides the bordered surface, the
 * "hidden until laid out" flash-avoidance dance, and dismissal wiring (barrier +
 * click-outside + escape). The actual content (a ProseMirror editor, a JSON
 * viewer, …) is injected by the renderer adapter via `bind`, so the popup never
 * knows what it is showing — edit vs read-only is inherited from that content.
 *
 * Extracted from the original text-only `TextCellPopup`.
 */
@component({ name: "std-cell-expand-popup" })
export class CellExpandPopup extends LonaWebComponent {
  #$content: HTMLElement = this.$("content");
  #props: Option<CellExpandPopup.Props> = null;

  bind(props: CellExpandPopup.Props): this {
    this.toggleAttribute("pending-layout", true);
    this.assignStyles({
      height: `${props.heightPx}px`,
      "--popup-bg": props.background,
    });
    this.#props = props;

    this.#clearDismissal();
    DomUtils.clearChildren(this.#$content);
    // The `#content` box owns the popup's interior size; the injected content
    // sizes itself within it (a block list via its own height/margins, an
    // editor via the adapter setting `height: 100%`). The shell MUST NOT force
    // sizing onto the content — doing so fights self-sizing content (e.g. the
    // data list's `calc(100% - 2px)` + margins) and clips it on scroll.
    this.#$content.appendChild(props.$content);

    this.#setupDismissal();
    return this;
  }

  async waitForLayout(): Promise<void> {
    await this.#props?.onWaitForLayout();
  }

  revealAfterLayout(): void {
    this.toggleAttribute("pending-layout", false);
  }

  focusContent(): void {
    this.#props?.onFocus();
  }

  #setupDismissal(): void {
    const dismiss = () => {
      this.#clearDismissal();
      this.#props?.onDismiss();
    };
    GESTURE_MANAGER.addBarrier(this);
    GESTURE_MANAGER.setOnClickOutside(this, dismiss);
    Hotkeys.pushEscapeHandler(this, dismiss);
  }

  #clearDismissal(): void {
    Hotkeys.clearEscapeHandler(this);
    GESTURE_MANAGER.clearOnClickOutside(this);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.#clearDismissal();
  }

  onRecycle(): void {
    super.onRecycle();
    this.#clearDismissal();
    this.#props?.onRecycleContent();
    this.#props = null;
    DomUtils.clearChildren(this.#$content);
  }

  static $styles: Style.Compat[] = [
    css`
      :host {
        display: block;
        margin-block: 2px;
        margin-inline: 3px;
        overflow: hidden;
        position: relative;
        background: var(--bg);
        border-radius: 8px;
        --additional-width: -6px;
      }

      :host([pending-layout]) {
        visibility: hidden;
      }

      #content {
        height: 100%;
        position: relative;
        z-index: 1;
        width: 100%;
      }

      :host::before {
        position: absolute;
        content: "";
        inset: 0;
        border: 2px solid var(--blue);
        border-radius: 8px;
        background: var(--popup-bg);
        box-shadow: var(--box-shadow);
        pointer-events: none;
      }
    `,
  ];

  static $html = html`<div id="content"></div>`;
}

export namespace CellExpandPopup {
  export type Props = {
    /** Popup height in px (row-aligned by the expander). */
    heightPx: number;
    /** Surface fill colour behind the content (renderer-chosen). */
    background: string;
    /** The content element to host. Edit/read-only is inherited from it. */
    $content: HTMLElement;
    /** Resolve once the content has real layout (gates the reveal). */
    onWaitForLayout: () => Promise<void>;
    /** Move focus into the content after reveal. */
    onFocus: () => void;
    /** Tear the content down when the popup is recycled. */
    onRecycleContent: () => void;
    /** Called when the popup is dismissed (click-outside / escape). */
    onDismiss: () => void;
  };

  /** Generic "content has real layout" probe for content that has no editor-
   *  style `waitForLayout` of its own (e.g. a synchronously-rendered list). */
  export async function waitForElementLayout($el: HTMLElement): Promise<void> {
    for (let i = 0; i < 5; i++) {
      await nextAnimationFrame();
      if ($el.isConnected && $el.clientHeight > 0) return;
    }
  }

  function nextAnimationFrame(): Promise<void> {
    return new Promise((resolve) => {
      window.requestAnimationFrame(() => resolve());
    });
  }
}
